import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { getPageMeta } from '@/lib/page-meta';

/**
 * Titre de page + fil d'Ariane, en tête du CONTENU — MOBILE UNIQUEMENT.
 *
 * Dans l'en-tête, sur un téléphone, ils se battaient pour la place avec le
 * portefeuille, le panier, les notifications et l'avatar : le fil finissait
 * tronqué (« Tableau de bord / Bouti… ») et le titre collait aux icônes. Ici
 * ils ont toute la largeur, et le fil devient cliquable.
 *
 * À partir de `lg`, RIEN : l'en-tête a la place de les porter et les garde,
 * comme avant (`ElectricianHeader`). Les deux rendus lisent la même table,
 * `lib/page-meta`.
 */
export function PageHeading() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const meta = getPageMeta(pathname);
  if (!meta) return null;

  const title = t(meta.title.key, meta.title.fallback);
  const trail = [...meta.parents, meta.title];

  return (
    <div className="lg:hidden pt-1 pb-5">
      <h1
        className="text-[24px] font-semibold leading-tight"
        style={{ fontFamily: 'Figtree', color: 'var(--pro-text-dark)' }}
      >
        {title}
      </h1>

      {meta.parents.length > 0 && (
        <nav
          aria-label={t('pro.page.breadcrumb', 'Fil d’Ariane')}
          className="mt-1"
        >
          <ol
            className="flex flex-wrap items-center gap-x-1.5 text-[13px]"
            style={{ fontFamily: 'Figtree' }}
          >
            {trail.map((crumb, i) => {
              const isLast = i === trail.length - 1;
              const label = t(crumb.key, crumb.fallback);
              return (
                <li key={`${crumb.key}-${i}`} className="flex items-center gap-1.5">
                  {crumb.to && !isLast ? (
                    <Link
                      to={crumb.to}
                      className="hover:underline"
                      style={{ color: 'var(--pro-primary)' }}
                    >
                      {label}
                    </Link>
                  ) : (
                    <span
                      style={{ color: 'var(--pro-text-grey)' }}
                      {...(isLast ? { 'aria-current': 'page' as const } : {})}
                    >
                      {label}
                    </span>
                  )}
                  {!isLast && (
                    <span
                      aria-hidden="true"
                      style={{ color: 'var(--pro-text-grey-light)' }}
                    >
                      /
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
    </div>
  );
}
